/* eslint-disable max-len */
import {
  conditionType,
  inputType
} from '../../../../../pages/esgManager/interface/inputType';
import {fixRowDependency} from '../../../Utils/CDP/corporate/questionDependency';
import {COUNTRY_OPTIONS, OPPORTUNITY_OPTIONS} from '../SME/smeQuestions';

import {
  OPPORTUNITY_TYPE_PRIMARY_ENVIRONMENTAL_OPPORTUNITY_DRIVER_OPTIONS,
  PRIMARY_RESPONSE_TO_RISK_OPTIONS,
  PROJECT_OPTIONS,
  RISK_IDENTIFIED_OPTIONS,
  RISK_TYPE_AND_PRIMARY_ENVIRONMENTAL_RISK_DRIVER_OPTIONS,
  RIVER_BASIN_OPTIONS
} from './corporateOptions';
import {
  CDP_CORPORATE_INTRO_FIELD_NAMES,
  CORPORATE_DISCLOSURE_FIELD_NAMES
} from './corporateQuestionsKeys';

export const corporateDisclosureQuestions = [
  {
    key: '1',
    showIndex: false,
    items: [
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '3.1 Have you identified any environmental risks which have had a substantive effect on your organization in the reporting year, or are anticipated to have a substantive effect on your organization in the future?',
            tooltip:
              'It is crucial for investors and data users to understand your organizations risk exposure, especially regarding any environmental risks across your value chain which are anticipated to have substantive effects on your organization. This includes both risks which have already had an effect within the reporting year and risks which may have an effect in the future. If no environmental risks with substantive effects have been identified as part of your assessment, it is equally crucial for data users to understand how and why you have concluded that such risks are not anticipated to affect your organization. '
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q1_VALUE,
            allowMultipleRows: false,
            formList: {
              formFields: [
                {
                  type: inputType.SELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q1_1_VALUE,
                  fieldLabel: 'Environmental issue',
                  options: [
                    {value: 'Climate change', label: 'Climate change'},
                    {value: 'Forests', label: 'Forests'},
                    {value: 'Water', label: 'Water'},
                    {value: 'Plastics', label: 'Plastics'},
                    {
                      value: 'Biodiversity [M-B only]',
                      label: 'Biodiversity [M-B only]'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q1_2_VALUE,
                  fieldLabel: 'Environmental risks identified',
                  options: [
                    {
                      value:
                        'Yes, both in direct operations and upstream/downstream value chain [not shown to FS]',
                      label:
                        'Yes, both in direct operations and upstream/downstream value chain [not shown to FS]'
                    },
                    {
                      value:
                        'Yes, only within our direct operations [not shown to FS]',
                      label:
                        'Yes, only within our direct operations [not shown to FS]'
                    },
                    {
                      value:
                        'Yes, only in our upstream/downstream value chain (excluding direct operations) [not shown to FS]',
                      label:
                        'Yes, only in our upstream/downstream value chain (excluding direct operations) [not shown to FS]'
                    },
                    {
                      value: 'Yes, only in our portfolio [FS only]',
                      label: 'Yes, only in our portfolio [FS only]'
                    },
                    {
                      value:
                        'Yes, both within our direct operations or upstream value chain, and within our portfolio [FS only]',
                      label:
                        'Yes, both within our direct operations or upstream value chain, and within our portfolio [FS only]'
                    },
                    {
                      value:
                        'Yes, only within our direct operations or upstream value chain [FS only]',
                      label:
                        'Yes, only within our direct operations or upstream value chain [FS only]'
                    },
                    {value: 'No', label: 'No'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q1_3_VALUE,
                  fieldLabel:
                    'Primary reason why your organization does not consider itself to have environmental risks in your direct operations and/or upstream/downstream value chain',
                  options: [
                    {
                      value:
                        'Environmental risks exist, but none with the potential to have a substantive effect on our organization',
                      label:
                        'Environmental risks exist, but none with the potential to have a substantive effect on our organization'
                    },
                    {
                      value: 'Evaluation in progress',
                      label: 'Evaluation in progress'
                    },
                    {
                      value:
                        'Lack of internal resources, capabilities, or expertise (e.g., due to organization size)',
                      label:
                        'Lack of internal resources, capabilities, or expertise (e.g., due to organization size)'
                    },
                    {
                      value: 'No standardized procedure',
                      label: 'No standardized procedure'
                    },
                    {
                      value: 'Not an immediate strategic priority',
                      label: 'Not an immediate strategic priority'
                    },
                    {value: 'Insufficient data', label: 'Insufficient data'},
                    {
                      value: 'Other, please specify',
                      label: 'Other, please specify'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q1_4_VALUE,
                  fieldLabel: 'Please explain',
                  colSpan: true,
                  textLimit: 2500,
                  disabledOn: {
                    fieldName:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q1_2_VALUE,
                    condition: conditionType.NOT,
                    values: [
                      'Yes, both in direct operations and upstream/downstream value chain [not shown to FS]',
                      'Yes, both within our direct operations or upstream value chain, and within our portfolio [FS only]'
                    ]
                  },
                  requiredOn: {
                    fieldName:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q1_2_VALUE,
                    condition: conditionType.EQUALS,
                    values: [
                      'Yes, both in direct operations and upstream/downstream value chain [not shown to FS]',
                      'Yes, both within our direct operations or upstream value chain, and within our portfolio [FS only]'
                    ],
                    dependentValue:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q1_2_VALUE
                  }
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '3.1.1 Provide details of the environmental risks identified which have had a substantive effect on your organization in the reporting year, or are anticipated to have a substantive effect on your organization in the future.',
            tooltip:
              'Your response to this question provides data users with an overview of the environmental risks affecting your organization now and in the future. Information on the current and anticipated financial effects of these risks, and your response strategy to manage and mitigate them, are critical elements for guiding investments as well as implementing corporate actions to improve business resilience and environmental stewardship. Ambition The organization discloses environmental risks which have had or are anticipated to have a substantive effect on the organization’s strategy, business activities, value chain, financial position (e.g. assets and liabilities), financial performance (e.g., revenue or expenditure) and cash flows. The disclosure per risk includes: o The timeframe, likelihood and magnitude associated with the effects of the risk, o Current and anticipated effects on the value chain and where these risks are located, o Whether the risks are physical (acute or chronic) or transition risks (policy, market, reputation, technology, liability), The current and anticipated financial effects of the risks, The costs of response to a risk, a description of the response and an explanation of the cost calculation, o (FS Sector) For financial institutions, environmental risks drivers are mapped to other risk management categories (e.g. credit risk, market risks, operational risk). (Financial Services sector) Financial institutions assess their portfolio for environmental risks, as well as measure and disclose the current and anticipated financial effect of the risks. o o • 138',
            conditionalVisibility: {
              dependsOn:
                CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q1_VALUE,
              showWhen: [
                'Yes, both in direct operations and upstream/downstream value chain [not shown to FS]',
                'Yes, only within our direct operations [not shown to FS]',
                'Yes, only in our upstream/downstream value chain (excluding direct operations) [not shown to FS]',
                'Yes, only in our portfolio [FS only]',
                'Yes, both within our direct operations or upstream value chain, and within our portfolio [FS only]',
                'Yes, only within our direct operations or upstream value chain [FS only]'
              ],
              condition: conditionType.EQUALS,
              fieldName:
                CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q1_2_VALUE
            }
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q2_VALUE,
            dependsOn:
              CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q1_2_VALUE,
            showWhen: [
              'Yes, both in direct operations and upstream/downstream value chain [not shown to FS]',
              'Yes, only within our direct operations [not shown to FS]',
              'Yes, only in our upstream/downstream value chain (excluding direct operations) [not shown to FS]',
              'Yes, only in our portfolio [FS only]',
              'Yes, both within our direct operations or upstream value chain, and within our portfolio [FS only]',
              'Yes, only within our direct operations or upstream value chain [FS only]'
            ],
            relatedTableKey:
              CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q1_VALUE,
            formList: {
              formFields: [
                {
                  type: inputType.SELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q2_1_VALUE,
                  fieldLabel: 'Environmental issue the risk relates to',
                  options: [
                    {value: 'Climate change', label: 'Climate change'},
                    {value: 'Forests', label: 'Forests'},
                    {value: 'Water', label: 'Water'},
                    {value: 'Plastics', label: 'Plastics'},
                    {
                      value: 'Biodiversity [M-B only]',
                      label: 'Biodiversity [M-B only]'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q2_2_VALUE,
                  fieldLabel: 'Risk identified',
                  options: RISK_IDENTIFIED_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q2_3_VALUE,
                  fieldLabel: 'Commodity',
                  options: [
                    {value: 'Timber products', label: 'Timber products'},
                    {value: 'Palm oil', label: 'Palm oil'},
                    {value: 'Cattle products', label: 'Cattle products'},
                    {value: 'Cocoa', label: 'Cocoa'},
                    {value: 'Coffee', label: 'Coffee'},
                    {value: 'Soy', label: 'Soy'},
                    {value: 'Rubber', label: 'Rubber'},
                    {
                      value: 'Other, please specify [FS only]',
                      label: 'Other, please specify [FS only]'
                    },
                    {value: 'Not applicable', label: 'Not applicable'},
                    {value: 'Water — N/A', label: 'Water — N/A'},
                    {value: 'Plastics — N/A', label: 'Plastics — N/A'},
                    {
                      value: 'Biodiversity [M-B only] — N/A',
                      label: 'Biodiversity [M-B only] — N/A'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q2_4_VALUE,
                  fieldLabel: 'Risk type and primary environmental risk driver',
                  options:
                    RISK_TYPE_AND_PRIMARY_ENVIRONMENTAL_RISK_DRIVER_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q2_5_VALUE,
                  fieldLabel: 'Value chain stage where the risk occurs',
                  options: [
                    {value: 'Direct operations', label: 'Direct operations'},
                    {
                      value: 'Upstream value chain',
                      label: 'Upstream value chain'
                    },
                    {
                      value: 'Downstream value chain [not shown to FS]',
                      label: 'Downstream value chain [not shown to FS]'
                    },
                    {
                      value: 'Banking (Bank) portfolio [FS only]',
                      label: 'Banking (Bank) portfolio [FS only]'
                    },
                    {
                      value: 'Investing (Asset manager) portfolio [FS only]',
                      label: 'Investing (Asset manager) portfolio [FS only]'
                    },
                    {
                      value: 'Investing (Asset owner) portfolio [FS only]',
                      label: 'Investing (Asset owner) portfolio [FS only]'
                    },
                    {
                      value: 'Insurance underwriting portfolio [FS only]',
                      label: 'Insurance underwriting portfolio [FS only]'
                    },
                    {
                      value: 'End-of-life management [P only]',
                      label: 'End-of-life management [P only]'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q2_6_VALUE,
                  fieldLabel:
                    'Risk type mapped to traditional financial services industry risk classification',
                  options: [
                    {
                      value: 'Capital adequacy and risk-weighted assets',
                      label: 'Capital adequacy and risk-weighted assets'
                    },
                    {value: 'Liquidity risk', label: 'Liquidity risk'},
                    {value: 'Funding risk', label: 'Funding risk'},
                    {value: 'Market risk', label: 'Market risk'},
                    {value: 'Credit risk', label: 'Credit risk'},
                    {value: 'Insurance risk', label: 'Insurance risk'},
                    {value: 'Reputational risk', label: 'Reputational risk'},
                    {
                      value: 'Policy and legal risk',
                      label: 'Policy and legal risk'
                    },
                    {value: 'Systemic risk', label: 'Systemic risk'},
                    {value: 'Operational risk', label: 'Operational risk'},
                    {value: 'Strategic risk', label: 'Strategic risk'},
                    {
                      value: 'Other non-financial risk',
                      label: 'Other non-financial risk'
                    },
                    {value: 'None', label: 'None'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q2_7_VALUE,
                  fieldLabel: 'Country/area where the risk occurs',
                  options: COUNTRY_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q2_8_VALUE,
                  fieldLabel: 'River basin where the risk occurs',
                  options: RIVER_BASIN_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q2_9_VALUE,
                  fieldLabel: 'Mining project ID',
                  options: [
                    {
                      value: 'All disclosed mining projects',
                      label: 'All disclosed mining projects'
                    },
                    {value: 'Projects 1–70', label: 'Projects 1–70'},
                    {value: 'N/A', label: 'N/A'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q2_10_VALUE,
                  fieldLabel: 'Organizationspecific description of risk',
                  colSpan: true,
                  textLimit: 1500
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q2_11_VALUE,
                  fieldLabel: '% of portfolio value vulnerable to this risk ',
                  options: [
                    {value: 'Less than 1%', label: 'Less than 1%'},
                    {value: '1-10', label: '1-10'},
                    {value: '11-20', label: '11-20'},
                    {value: '21-30', label: '21-30'},
                    {value: '31-40', label: '31-40'},
                    {value: '41-50', label: '41-50'},
                    {value: '51-60', label: '51-60'},
                    {value: '61-70', label: '61-70'},
                    {value: '71-80', label: '71-80'},
                    {value: '81-90', label: '81-90'},
                    {value: '91-99', label: '91-99'},
                    {value: '100%', label: '100%'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q2_12_VALUE,
                  fieldLabel: 'Primary financial effect of the risk',
                  options: [
                    {value: 'Brand damage', label: 'Brand damage'},
                    {
                      value: 'Change in revenue mix and sources',
                      label: 'Change in revenue mix and sources'
                    },
                    {
                      value: 'Closure of operations',
                      label: 'Closure of operations'
                    },
                    {
                      value: 'Constraint to growth',
                      label: 'Constraint to growth'
                    },
                    {
                      value: 'Decrease in shareholder value',
                      label: 'Decrease in shareholder value'
                    },
                    {
                      value: 'Decreased access to capital',
                      label: 'Decreased access to capital'
                    },
                    {
                      value:
                        'Decreased asset value or asset useful life leading to write-offs, asset impairment or early retirement of existing assets',
                      label:
                        'Decreased asset value or asset useful life leading to write-offs, asset impairment or early retirement of existing assets'
                    },
                    {
                      value:
                        'Decreased revenues due to reduced demand for products and services',
                      label:
                        'Decreased revenues due to reduced demand for products and services'
                    },
                    {
                      value:
                        'Decreased revenues due to reduced production capacity',
                      label:
                        'Decreased revenues due to reduced production capacity'
                    },
                    {
                      value: 'Delays in securing operating licenses',
                      label: 'Delays in securing operating licenses'
                    },
                    {
                      value:
                        'Devaluation of collateral and potential for stranded, illiquid assets [FS only]',
                      label:
                        'Devaluation of collateral and potential for stranded, illiquid assets [FS only]'
                    },
                    {
                      value: 'Disruption in production capacity',
                      label: 'Disruption in production capacity'
                    },
                    {
                      value: 'Disruption to sales',
                      label: 'Disruption to sales'
                    },
                    {
                      value: 'Disruption in upstream value chain',
                      label: 'Disruption in upstream value chain'
                    },
                    {
                      value: 'Disruption to workforce management and planning',
                      label: 'Disruption to workforce management and planning'
                    },
                    {
                      value: 'Fines, penalties or enforcement orders',
                      label: 'Fines, penalties or enforcement orders'
                    },
                    {
                      value: 'Increased capital expenditures',
                      label: 'Increased capital expenditures'
                    },
                    {
                      value: 'Increased compliance costs',
                      label: 'Increased compliance costs'
                    },
                    {
                      value: 'Increased cost of capital',
                      label: 'Increased cost of capital'
                    },
                    {
                      value: 'Increased credit risk',
                      label: 'Increased credit risk'
                    },
                    {
                      value: 'Increased direct costs',
                      label: 'Increased direct costs'
                    },
                    {
                      value: 'Increased indirect [operating] costs',
                      label: 'Increased indirect [operating] costs'
                    },
                    {
                      value: 'Increased insurance claims liability [FS only]',
                      label: 'Increased insurance claims liability [FS only]'
                    },
                    {
                      value: 'Increased insurance premiums',
                      label: 'Increased insurance premiums'
                    },
                    {
                      value: 'Increased production costs',
                      label: 'Increased production costs'
                    },
                    {value: 'Litigation', label: 'Litigation'},
                    {
                      value: 'Loss of license to operate',
                      label: 'Loss of license to operate'
                    },
                    {
                      value: 'Mine closure [MM, CO, M-B only]',
                      label: 'Mine closure [MM, CO, M-B only]'
                    },
                    {
                      value:
                        'Reduced availability of insurance on assets in “high-risk” locations',
                      label:
                        'Reduced availability of insurance on assets in “high-risk” locations'
                    },
                    {
                      value:
                        'Reduced profitability of investment portfolios [FS only]',
                      label:
                        'Reduced profitability of investment portfolios [FS only]'
                    },
                    {
                      value:
                        'Upfront costs to adopt/deploy new practices and processes',
                      label:
                        'Upfront costs to adopt/deploy new practices and processes'
                    },
                    {
                      value: 'Other, please specify',
                      label: 'Other, please specify'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q2_13_VALUE,
                  fieldLabel:
                    'Time horizon over which the risk is anticipated to have a substantive effect on the organization',
                  options: [
                    {value: 'Short-term', label: 'Short-term'},
                    {value: 'Medium-term', label: 'Medium-term'},
                    {value: 'Long-term', label: 'Long-term'},
                    {
                      value:
                        'The risk has already had a substantive effect on the organization in the reporting year',
                      label:
                        'The risk has already had a substantive effect on the organization in the reporting year'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q2_14_VALUE,
                  fieldLabel:
                    'Likelihood of the risk having an effect within the anticipated time horizon',
                  options: [
                    {value: 'Virtually certain', label: 'Virtually certain'},
                    {value: 'Very likely', label: 'Very likely'},
                    {value: 'Likely', label: 'Likely'},
                    {
                      value: 'More likely than not',
                      label: 'More likely than not'
                    },
                    {
                      value: 'About as likely as not',
                      label: 'About as likely as not'
                    },
                    {value: 'Unlikely', label: 'Unlikely'},
                    {value: 'Very unlikely', label: 'Very unlikely'},
                    {
                      value: 'Exceptionally unlikely',
                      label: 'Exceptionally unlikely'
                    },
                    {value: 'Unknown', label: 'Unknown'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q2_15_VALUE,
                  fieldLabel: 'Magnitude',
                  options: [
                    {value: 'High', label: 'High'},
                    {value: 'Medium-high', label: 'Medium-high'},
                    {value: 'Medium', label: 'Medium'},
                    {value: 'Medium-low', label: 'Medium-low'},
                    {value: 'Low', label: 'Low'},
                    {value: 'Unknown', label: 'Unknown'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q2_16_VALUE,
                  fieldLabel:
                    'Effect of the risk on the financial position, financial performance and cash flows of the organization in the reporting year',
                  colSpan: true,
                  textLimit: 2500
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q2_17_VALUE,
                  fieldLabel:
                    'Anticipated effect of the risk on the financial position, financial performance and cash flows of the organization in the selected future time horizons',
                  colSpan: true,
                  textLimit: 2500
                },
                {
                  type: inputType.RADIOGROUP,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q2_18_VALUE,
                  fieldLabel:
                    'Are you able to quantify the financial effect of the risk?',
                  options: ['Yes', 'No'],
                  colSpan: true
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q2_19_VALUE,
                  fieldLabel:
                    'Financial effect figure in the reporting year (currency)',
                  colSpan: true,
                  roundTo: 2,
                  disabledOn: {
                    fieldName:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q2_18_VALUE,
                    condition: conditionType.NOT,
                    values: ['Yes']
                  }
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q2_20_VALUE,
                  fieldLabel:
                    'Anticipated financial effect figure in the short-term – minimum (currency)',
                  colSpan: true,
                  roundTo: 2
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q2_21_VALUE,
                  fieldLabel:
                    'Anticipated financial effect figure in the short-term – maximum (currency)',
                  colSpan: true,
                  roundTo: 2
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q2_22_VALUE,
                  fieldLabel:
                    'Anticipated financial effect figure in the medium-term – minimum (currency)',
                  colSpan: true,
                  roundTo: 2
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q2_23_VALUE,
                  fieldLabel:
                    'Anticipated financial effect figure in the medium-term – maximum (currency)',
                  colSpan: true,
                  roundTo: 2
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q2_24_VALUE,
                  fieldLabel:
                    'Anticipated financial effect figure in the long-term – minimum (currency)',
                  colSpan: true,
                  roundTo: 2
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q2_25_VALUE,
                  fieldLabel:
                    'Anticipated financial effect figure in the long-term – maximum (currency)',
                  colSpan: true,
                  roundTo: 2,
                  disabledOn: {
                    fieldName:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q2_18_VALUE,
                    condition: conditionType.NOT,
                    values: ['Yes']
                  }
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q2_26_VALUE,
                  fieldLabel: 'Explanation of financial effect figure',
                  colSpan: true,
                  textLimit: 2500
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q2_27_VALUE,
                  fieldLabel: 'Primary response to risk',
                  options: PRIMARY_RESPONSE_TO_RISK_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q2_28_VALUE,
                  fieldLabel: 'Cost of response to risk',
                  colSpan: true,
                  roundTo: 2,
                  disabledOn: {
                    fieldName:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q2_1_VALUE,
                    condition: conditionType.NOT,
                    values: [
                      'Climate change',
                      'Forests',
                      'Water',
                      'Biodiversity [M-B only]'
                    ]
                  }
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q2_29_VALUE,
                  fieldLabel: 'Explanation of cost calculation',
                  colSpan: true,
                  textLimit: 1000,
                  disabledOn: {
                    fieldName:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q2_1_VALUE,
                    condition: conditionType.NOT,
                    values: [
                      'Climate change',
                      'Forests',
                      'Water',
                      'Biodiversity [M-B only]'
                    ]
                  }
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q2_30_VALUE,
                  fieldLabel: 'Description of response',
                  colSpan: true,
                  textLimit: 2000
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '3.1.2 Provide the amount and proportion of your financial metrics from the reporting year that are vulnerable to the substantive effects of environmental risks.',
            tooltip:
              'Your response to this question provides data users with an overview of the environmental risks affecting your organization now and in the future. Information on the current and anticipated financial effects of these risks, and your response strategy to manage and mitigate them, are critical elements for guiding investments as well as implementing corporate actions to improve business resilience and environmental stewardship.',
            conditionalVisibility: {
              dependsOn:
                CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q1_VALUE,
              showWhen: [
                'Yes, both in direct operations and upstream/downstream value chain [not shown to FS]',
                'Yes, only within our direct operations [not shown to FS]',
                'Yes, only in our upstream/downstream value chain (excluding direct operations) [not shown to FS]',
                'Yes, only in our portfolio [FS only]',
                'Yes, both within our direct operations or upstream value chain, and within our portfolio [FS only]',
                'Yes, only within our direct operations or upstream value chain [FS only]'
              ],
              condition: conditionType.EQUALS,
              fieldName:
                CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q1_2_VALUE
            }
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q3_VALUE,
            dependsOn:
              CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q1_2_VALUE,
            showWhen: [
              'Yes, both in direct operations and upstream/downstream value chain [not shown to FS]',
              'Yes, only within our direct operations [not shown to FS]',
              'Yes, only in our upstream/downstream value chain (excluding direct operations) [not shown to FS]',
              'Yes, only in our portfolio [FS only]',
              'Yes, both within our direct operations or upstream value chain, and within our portfolio [FS only]',
              'Yes, only within our direct operations or upstream value chain [FS only]'
            ],
            relatedTableKey:
              CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q1_VALUE,
            formList: {
              formFields: [
                {
                  type: inputType.SELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q3_1_VALUE,
                  fieldLabel: 'Environmental issue ',
                  options: [
                    {value: 'Climate change', label: 'Climate change'},
                    {value: 'Forests', label: 'Forests'},
                    {value: 'Water', label: 'Water'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q3_2_VALUE,
                  fieldLabel: 'Financial metric',
                  options: [
                    {value: 'Assets', label: 'Assets'},
                    {value: 'Liabilities', label: 'Liabilities'},
                    {value: 'Revenue', label: 'Revenue'},
                    {value: 'CAPEX', label: 'CAPEX'},
                    {value: 'OPEX', label: 'OPEX'},
                    {
                      value: 'Other, please specify',
                      label: 'Other, please specify'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q3_3_VALUE,
                  fieldLabel:
                    'Amount of financial metric vulnerable to transition risks for this environmental issue (unit currency as selected in 1.2)',
                  colSpan: true,
                  roundTo: 2
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q3_4_VALUE,
                  fieldLabel:
                    '% of total financial metric vulnerable to transition risks for this environmental issue',
                  options: [
                    {value: 'Less than 1%', label: 'Less than 1%'},
                    {value: '1-10%', label: '1-10%'},
                    {value: '11-20%', label: '11-20%'},
                    {value: '21-30%', label: '21-30%'},
                    {value: '31-40%', label: '31-40%'},
                    {value: '41-50%', label: '41-50%'},
                    {value: '51-60%', label: '51-60%'},
                    {value: '61-70%', label: '61-70%'},
                    {value: '71-80%', label: '71-80%'},
                    {value: '81-90%', label: '81-90%'},
                    {value: '91-99%', label: '91-99%'},
                    {value: '100%', label: '100%'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q3_5_VALUE,
                  fieldLabel:
                    'Amount of financial metric vulnerable to physical risks for this environmental issue (unit currency as selected in 1.2)',
                  colSpan: true,
                  roundTo: 2
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q3_6_VALUE,
                  fieldLabel:
                    '% of total financial metric vulnerable to physical risks for this environmental issue',
                  options: [
                    {value: 'Less than 1%', label: 'Less than 1%'},
                    {value: '1-10%', label: '1-10%'},
                    {value: '11-20%', label: '11-20%'},
                    {value: '21-30%', label: '21-30%'},
                    {value: '31-40%', label: '31-40%'},
                    {value: '41-50%', label: '41-50%'},
                    {value: '51-60%', label: '51-60%'},
                    {value: '61-70%', label: '61-70%'},
                    {value: '71-80%', label: '71-80%'},
                    {value: '81-90%', label: '81-90%'},
                    {value: '91-99%', label: '91-99%'},
                    {value: '100%', label: '100%'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q3_7_VALUE,
                  fieldLabel:
                    'Amount of CAPEX in the reporting year deployedtowards risks related to this environmental issue',
                  colSpan: true,
                  roundTo: 2,
                  disabledOn: {
                    fieldName:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q3_2_VALUE,
                    condition: conditionType.NOT,
                    values: ['CAPEX']
                  },
                  requiredOn: {
                    fieldName:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q3_2_VALUE,
                    condition: conditionType.EQUALS,
                    values: ['CAPEX'],
                    required: true,
                    dependentValue:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q3_2_VALUE
                  }
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q3_8_VALUE,
                  fieldLabel: 'Explanation of financial figures',
                  colSpan: true,
                  textLimit: 2500
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '3.2 Within each river basin, how many facilities are exposed to substantive effects of water-related risks, and what percentage of your total number of facilities does this represent?',
            tooltip:
              'Information on river basins provides a deeper understanding of the potential significance of the water risk exposure associated with each basin, which may be driven by the organization’s dependencies and impacts. Knowing the number of facilities at risk for each basin and the potential impact of this risk exposure helps organizations with decision making based on basin-level issues. It allows financial planning at that level to take account of water issues and provides a powerful indicator to support river basin action and investment.',
            conditionalVisibility: {
              dependsOn:
                CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q1_VALUE,
              showWhen: [
                'Yes, both in direct operations and upstream/downstream value chain [not shown to FS]',
                'Yes, only within our direct operations [not shown to FS]',
                'Yes, only in our upstream/downstream value chain (excluding direct operations) [not shown to FS]',
                'Yes, only in our portfolio [FS only]',
                'Yes, both within our direct operations or upstream value chain, and within our portfolio [FS only]',
                'Yes, only within our direct operations or upstream value chain [FS only]'
              ],
              condition: conditionType.EQUALS,
              fieldName:
                CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q1_2_VALUE
            }
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q4_VALUE,
            dependsOn:
              CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q1_2_VALUE,
            showWhen: [
              'Yes, both in direct operations and upstream/downstream value chain [not shown to FS]',
              'Yes, only within our direct operations [not shown to FS]',
              'Yes, only in our upstream/downstream value chain (excluding direct operations) [not shown to FS]',
              'Yes, only in our portfolio [FS only]',
              'Yes, both within our direct operations or upstream value chain, and within our portfolio [FS only]',
              'Yes, only within our direct operations or upstream value chain [FS only]'
            ],
            relatedTableKey:
              CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q1_VALUE,
            formList: {
              formFields: [
                {
                  type: inputType.SELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q4_1_VALUE,
                  fieldLabel: 'Country/Area & River basin',
                  options: RIVER_BASIN_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q4_2_VALUE,
                  fieldLabel:
                    'Value chain stages where facilities at risk have been identified in this river basin ',
                  options: [
                    {value: 'Direct operations', label: 'Direct operations'},
                    {
                      value: 'Downstream value chain',
                      label: 'Downstream value chain'
                    },
                    {
                      value: 'Upstream value chain',
                      label: 'Upstream value chain'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q4_3_VALUE,
                  fieldLabel:
                    'Number of facilities within direct operations exposed to water-related risk in this river basin',
                  colSpan: true,
                  roundTo: 2,
                  disabledOn: {
                    fieldName:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q4_2_VALUE,
                    condition: conditionType.NOT_INCLUDES,
                    values: ['Direct operations']
                  },
                  requiredOn: {
                    fieldName:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q4_2_VALUE,
                    condition: conditionType.EQUALS,
                    values: ['Direct operations'],
                    required: true,
                    dependentValue:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q4_2_VALUE
                  }
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q4_4_VALUE,
                  fieldLabel:
                    '% of your organization’s total facilities within direct operations exposed to waterrelated risk in this river basin',
                  options: [
                    {value: '1–25%', label: '1–25%'},
                    {value: '26–50%', label: '26–50%'},
                    {value: '51–75%', label: '51–75%'},
                    {value: '76–99%', label: '76–99%'},
                    {value: '100%', label: '100%'},
                    {value: 'Unknown', label: 'Unknown'}
                  ],
                  colSpan: true,
                  disabledOn: {
                    fieldName:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q4_2_VALUE,
                    condition: conditionType.NOT_INCLUDES,
                    values: ['Direct operations']
                  },
                  requiredOn: {
                    fieldName:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q4_2_VALUE,
                    condition: conditionType.EQUALS,
                    values: ['Direct operations'],
                    required: true,
                    dependentValue:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q4_2_VALUE
                  }
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q4_5_VALUE,
                  fieldLabel:
                    'Number of facilities within downstream value chain exposed to waterrelated risk in this river basin',
                  colSpan: true,
                  roundTo: 2
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q4_6_VALUE,
                  fieldLabel:
                    'Number of facilities within upstream value chain exposed to water-related risk in this river basin',
                  colSpan: true,
                  roundTo: 2
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q4_7_VALUE,
                  fieldLabel:
                    'Production value for the metals and mining activities associated with these facilities (currency)',
                  colSpan: true,
                  roundTo: 2
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q4_8_VALUE,
                  fieldLabel:
                    '% organization’s annual electricity generation that could be affected by these facilities ',
                  options: [
                    {value: '1–25%', label: '1–25%'},
                    {value: '26–50%', label: '26–50%'},
                    {value: '51–75%', label: '51–75%'},
                    {value: '76–99%', label: '76–99%'},
                    {value: '100%', label: '100%'},
                    {value: 'Unknown', label: 'Unknown'},
                    {value: 'Not applicable', label: 'Not applicable'}
                  ],
                  colSpan: true,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_INTRO_FIELD_NAMES.CORPORATE_INTRO_Q18_VALUE,
                    condition: conditionType.EQUALS,
                    values: ['Electricity generation']
                  },
                  requiredOn: {
                    fieldName:
                      CDP_CORPORATE_INTRO_FIELD_NAMES.CORPORATE_INTRO_Q18_VALUE,
                    condition: conditionType.NOT,
                    values: ['Electricity generation'],
                    dependentValue:
                      CDP_CORPORATE_INTRO_FIELD_NAMES.CORPORATE_INTRO_Q18_VALUE
                  }
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q4_9_VALUE,
                  fieldLabel:
                    '% organization’s global oil and gas production volume that could be affected by these facilities ',
                  options: [
                    {value: '1–25%', label: '1–25%'},
                    {value: '26–50%', label: '26–50%'},
                    {value: '51–75%', label: '51–75%'},
                    {value: '76–99%', label: '76–99%'},
                    {value: '100%', label: '100%'},
                    {value: 'Unknown', label: 'Unknown'},
                    {value: 'Not applicable', label: 'Not applicable'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q4_10_VALUE,
                  fieldLabel:
                    '% organization’s total global revenue that could be affected ',
                  options: [
                    {value: 'Less than 1%', label: 'Less than 1%'},
                    {value: '1-10%', label: '1-10%'},
                    {value: '11-20%', label: '11-20%'},
                    {value: '21-30%', label: '21-30%'},
                    {value: '31-40%', label: '31-40%'},
                    {value: '41-50%', label: '41-50%'},
                    {value: '51-60%', label: '51-60%'},
                    {value: '61-70%', label: '61-70%'},
                    {value: '71-80%', label: '71-80%'},
                    {value: '81-90%', label: '81-90%'},
                    {value: '91-99%', label: '91-99%'},
                    {value: '100%', label: '100%'},
                    {value: 'Unknown', label: 'Unknown'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q4_11_VALUE,
                  fieldLabel: 'Please explain',
                  colSpan: true,
                  textLimit: 2500
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '3.3 In the reporting year, was your organization subject to any fines, enforcement orders, and/or other penalties for water-related regulatory violations?',
            tooltip:
              'Knowledge of compliance related fines and other non-financial penalties across your organization demonstrates an awareness by your organization of its impact on the local operating environment as well as the potential financial implications of its water management. This information helps investors and other data users to gain some insight into the effectiveness of your control procedures at a corporate level.'
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q5_VALUE,
            allowMultipleRows: false,
            formList: {
              formFields: [
                {
                  type: inputType.SELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q5_1_VALUE,
                  fieldLabel: 'Water-related regulatory violations',
                  options: [
                    {value: 'Yes', label: 'Yes'},
                    {value: 'No', label: 'No'},
                    {value: 'Unknown', label: 'Unknown'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q5_2_VALUE,
                  fieldLabel:
                    'Fines, enforcement orders, and/or other penalties',
                  options: [
                    {label: 'Fines', value: 'Fines'},
                    {
                      label: 'Enforcement orders or other penalties',
                      value: 'Enforcement orders or other penalties'
                    },
                    {
                      label:
                        'Fines, but none that are considered as significant',
                      value:
                        'Fines, but none that are considered as significant'
                    },
                    {
                      label:
                        'Enforcement orders or other penalties but none that are considered as significant',
                      value:
                        'Enforcement orders or other penalties but none that are considered as significant'
                    }
                  ],
                  colSpan: true,
                  disabledOn: {
                    fieldName:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q5_1_VALUE,
                    condition: conditionType.EQUALS,
                    values: ['No']
                  }
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q5_3_VALUE,
                  fieldLabel: 'Comment',
                  colSpan: true,
                  textLimit: 1000,
                  disabledOn: {
                    fieldName:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q5_1_VALUE,
                    condition: conditionType.EQUALS,
                    values: ['No']
                  }
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '3.3.1 Provide the total number and financial value of all water-related fines',
            tooltip:
              'Providing aggregated information, for absolute numbers and the tracking of numbers of compliance-related fines, is evidence that your organization is monitoring its local operating environments and keeping track at corporate level.',
            conditionalVisibility: {
              dependsOn:
                CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q5_VALUE,
              showWhen: [
                'Fines',
                'Fines, but none that are considered as significant'
              ],
              condition: conditionType.EQUALS,
              fieldName:
                CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q5_2_VALUE
            }
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q6_VALUE,
            dependsOn:
              CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q5_2_VALUE,
            showWhen: [
              'Fines',
              'Fines, but none that are considered as significant'
            ],
            relatedTableKey:
              CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q5_VALUE,
            allowMultipleRows: false,
            formList: {
              formFields: [
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q6_1_VALUE,
                  fieldLabel: 'Total number of fines',
                  colSpan: true
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q6_2_VALUE,
                  fieldLabel: 'Total value of fines',
                  colSpan: true
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q6_3_VALUE,
                  fieldLabel: '% of total facilities/operations associated ',
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q6_4_VALUE,
                  fieldLabel: 'Number of fines compared to previous year',
                  options: [
                    {label: 'Much lower', value: 'Much lower'},
                    {label: 'Lower', value: 'Lower'},
                    {label: 'About the same', value: 'About the same'},
                    {label: 'Higher', value: 'Higher'},
                    {label: 'Much higher', value: 'Much higher'},
                    {
                      label: 'This is our first year of measurement',
                      value: 'This is our first year of measurement'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q6_5_VALUE,
                  fieldLabel: 'Comment',
                  colSpan: true,
                  textLimit: 1000
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '3.3.2 Provide details for all significant fines, enforcement orders, and/or penalties for water-related regulatory violations in the reporting year, and your plans for resolving them.',
            tooltip:
              'Providing these details of fines and other non-financial penalties, such as location and your plans for resolving them, demonstrates to CDP data users that your organization is keeping track at the corporate level and also that it has an awareness of impacts in their local operating environment.',
            conditionalVisibility: {
              dependsOn:
                CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q5_VALUE,
              showWhen: [
                'Fines',
                'Fines, but none that are considered as significant'
              ],
              condition: conditionType.EQUALS,
              fieldName:
                CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q5_2_VALUE
            }
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q7_VALUE,
            dependsOn:
              CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q5_2_VALUE,
            showWhen: [
              'Fines',
              'Fines, but none that are considered as significant'
            ],
            relatedTableKey:
              CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q5_VALUE,
            formList: {
              formFields: [
                {
                  type: inputType.SELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q7_1_VALUE,
                  fieldLabel: 'Type of penalty',
                  options: [
                    {label: 'Fine', value: 'Fine'},
                    {label: 'Enforcement order', value: 'Enforcement order'},
                    {
                      label: 'Other penalty type, please specify',
                      value: 'Other penalty type, please specify'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q7_2_VALUE,
                  fieldLabel: 'Financial impact',
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q7_3_VALUE,
                  fieldLabel: 'Country/Area & River basin',
                  options: RIVER_BASIN_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q7_4_VALUE,
                  fieldLabel: 'Type of incident',
                  options: [
                    {
                      label:
                        'Spillage, leakage or discharge of potential water pollutant',
                      value:
                        'Spillage, leakage or discharge of potential water pollutant'
                    },
                    {
                      label: 'Failure to monitor effluent',
                      value: 'Failure to monitor effluent'
                    },
                    {
                      label: 'Effluent limit exceedances',
                      value: 'Effluent limit exceedances'
                    },
                    {
                      label:
                        'Abstraction without a permit or abstraction that exceeded permit',
                      value:
                        'Abstraction without a permit or abstraction that exceeded permit'
                    },
                    {
                      label:
                        'Other non-compliance with permits, standards, or regulations',
                      value:
                        'Other non-compliance with permits, standards, or regulations'
                    },
                    {
                      label:
                        'Incorrect administration of permits, standards or regulations',
                      value:
                        'Incorrect administration of permits, standards or regulations'
                    },
                    {
                      label: 'Other, please specify',
                      value: 'Other, please specify'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q7_5_VALUE,
                  fieldLabel:
                    'Description of penalty, incident, regulatory violation, significance, and resolution',
                  colSpan: true,
                  textLimit: 1000
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '3.4 In the reporting year, was your organization subject to any fines, enforcement orders, and/or other penalties for violation of biodiversity-related regulation?',
            tooltip:
              'Knowledge of compliance related to fines and other non-financial penalties across your organization demonstrates an awareness of your organization’s impact on the local environment, as well as the potential financial implications of its biodiversity management. This information provides insights to investors and other data users about the effectiveness of your control procedures at a corporate level.'
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q8_VALUE,
            formList: {
              formFields: [
                {
                  type: inputType.SELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q8_1_VALUE,
                  fieldLabel:
                    'Any penalties for violation of biodiversity-related regulation?',
                  options: [
                    {label: 'Yes', value: 'Yes'},
                    {
                      label: 'Yes, but none considered significant',
                      value: 'Yes, but none considered significant'
                    },
                    {label: 'No', value: 'No'},
                    {label: 'Data not available', value: 'Data not available'},
                    {
                      label: 'Data available but not disclosing',
                      value: 'Data available but not disclosing'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q8_2_VALUE,
                  fieldLabel: 'Comment',
                  colSpan: true,
                  textLimit: 1000
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '3.4.1 Provide details for all significant fines, enforcement orders and/or other penalties for biodiversityrelated regulatory violations in the reporting year, and your plans for resolving them.',
            tooltip:
              'Providing details of significant fines and other non-financial penalties, such as location and your plans for resolving them, demonstrates to CDP data users that your organization is keeping track of these events at the corporate level and has awareness of unplanned impacts on biodiversity caused by its operations.',
            conditionalVisibility: {
              dependsOn:
                CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q8_VALUE,
              showWhen: ['Yes', 'Yes, but none considered significant'],
              condition: conditionType.EQUALS,
              fieldName:
                CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q8_1_VALUE
            }
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q9_VALUE,
            dependsOn:
              CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q8_1_VALUE,
            showWhen: ['Yes', 'Yes, but none considered significant'],
            relatedTableKey:
              CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q8_VALUE,
            formList: {
              formFields: [
                {
                  type: inputType.SELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q9_1_VALUE,
                  fieldLabel: 'Mining project ID',
                  options: PROJECT_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q9_2_VALUE,
                  fieldLabel: 'Type of penalty',
                  options: [
                    {label: 'Fine', value: 'Fine'},
                    {label: 'Enforcement order', value: 'Enforcement order'},
                    {
                      label: 'Other penalty type, please specify',
                      value: 'Other penalty type, please specify'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q9_3_VALUE,
                  fieldLabel: 'Financial value of penalty',
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q9_4_VALUE,
                  fieldLabel: 'Type of incident',
                  options: [
                    {
                      label: 'Unauthorized deforestation',
                      value: 'Unauthorized deforestation'
                    },
                    {
                      label:
                        'Unauthorized conversion of natural habitats (other than forests)',
                      value:
                        'Unauthorized conversion of natural habitats (other than forests)'
                    },
                    {
                      label: 'Damage to protected and/or threatened species',
                      value: 'Damage to protected and/or threatened species'
                    },
                    {
                      label: 'Damage to protected areas',
                      value: 'Damage to protected areas'
                    },
                    {
                      label: 'Tailing dam failure/accident',
                      value: 'Tailing dam failure/accident'
                    },
                    {
                      label: 'Pollution leading to wildlife mortality',
                      value: 'Pollution leading to wildlife mortality'
                    },
                    {
                      label: 'Other, please specify',
                      value: 'Other, please specify'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q9_5_VALUE,
                  fieldLabel: 'Please explain',
                  colSpan: true,
                  textLimit: 2400
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.SELECT,
            fieldName:
              CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q10_VALUE,
            fieldLabel:
              '3.5 Are any of your operations or activities regulated by a carbon pricing system (i.e. ETS, Cap & Trade or Carbon Tax)?',
            tooltip:
              'Companies are requested to report whether they are subject to, or potentially subject to, mandatory carbon pricing systems. This question has evolved to include whether companies are currently regulated by a carbon pricing system – including carbon markets or taxation – or whether they expect to be regulated in the future. Companies responding ”Yes” will be further prompted to identify the systems in which they participate and to provide additional details about their exposure to these systems. This information allows investors to consistently track and analyze corporate expectations and the associated costs of carbon pricing regulations, and forces unregulated companies to consider potential future exposure.',
            heading: inputType.HEADING,
            colSpan: true,
            options: [
              {label: 'Yes', value: 'Yes'},
              {
                label:
                  'No, but we anticipate being regulated in the next three years',
                value:
                  'No, but we anticipate being regulated in the next three years'
              },
              {
                label:
                  'No, and we do not anticipate being regulated in the next three years',
                value:
                  'No, and we do not anticipate being regulated in the next three years'
              }
            ]
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.MULTISELECT,
            fieldName:
              CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q11_VALUE,
            fieldLabel:
              '3.5.1 Select the carbon pricing regulations(s) which impact your operations.',
            tooltip:
              'As the number of jurisdictions with carbon pricing policies has doubled over the last decade, data users are interested in understanding how organizations are affected by these schemes. This question contextualizes the regulatory landscapes in which organizations operate and the potential for future regulation which may impact an organization’s operations. Select all that apply from the following options:',
            heading: inputType.HEADING,
            colSpan: true,
            //    conditionalVisibility: {
            dependsOn:
              CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q10_VALUE,
            showWhen: ['Yes'],
            condition: conditionType.EQUALS,
            // relatedTableKey:
            //   CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q10_VALUE,
            //    },
            options: [
              {label: 'Alberta TIER - ETS', value: 'Alberta TIER - ETS'},
              {
                label: 'Argentina carbon tax',
                value: 'Argentina carbon tax'
              },
              {
                label: 'Australia ERF Safeguard Mechanism – ETS',
                value: 'Australia ERF Safeguard Mechanism – ETS'
              },
              {label: 'Austria – ETS', value: 'Austria – ETS'},
              {
                label: 'Baja California carbon tax',
                value: 'Baja California carbon tax'
              },
              {label: 'BC carbon tax', value: 'BC carbon tax'},
              {label: 'BC GGIRCA – ETS', value: 'BC GGIRCA – ETS'},
              {label: 'Beijing pilot ETS', value: 'Beijing pilot ETS'},
              {
                label: 'California CaT – ETS',
                value: 'California CaT – ETS'
              },
              {
                label: 'Canada federal fuel charge',
                value: 'Canada federal fuel charge'
              },
              {
                label:
                  'Canada federal Output Based Pricing System (OBPS) – ETS',
                value: 'Canada federal Output Based Pricing System (OBPS) – ETS'
              },
              {label: 'Chile carbon tax', value: 'Chile carbon tax'},
              {label: 'China national ETS', value: 'China national ETS'},
              {
                label: 'Chongqing pilot ETS',
                value: 'Chongqing pilot ETS'
              },
              {
                label: 'Colombia carbon tax',
                value: 'Colombia carbon tax'
              },
              {label: 'Denmark carbon tax', value: 'Denmark carbon tax'},
              {label: 'Durango carbon tax', value: 'Durango carbon tax'},
              {label: 'Estonia carbon tax', value: 'Estonia carbon tax'},
              {label: 'EU ETS', value: 'EU ETS'},
              {label: 'Finland carbon tax', value: 'Finland carbon tax'},
              {label: 'France carbon tax', value: 'France carbon tax'},
              {label: 'Fujian pilot ETS', value: 'Fujian pilot ETS'},
              {label: 'Germany ETS', value: 'Germany ETS'},
              {
                label: 'Guanajuato carbon tax',
                value: 'Guanajuato carbon tax'
              },
              {
                label: 'Guangdong pilot ETS',
                value: 'Guangdong pilot ETS'
              },
              {label: 'Hubei pilot ETS', value: 'Hubei pilot ETS'},
              {label: 'Iceland carbon tax', value: 'Iceland carbon tax'},
              {label: 'Indonesia ETS', value: 'Indonesia ETS'},
              {label: 'Ireland carbon tax', value: 'Ireland carbon tax'},
              {label: 'Japan carbon tax', value: 'Japan carbon tax'},
              {label: 'Kazakhstan ETS', value: 'Kazakhstan ETS'},
              {label: 'Korea ETS', value: 'Korea ETS'},
              {label: 'Latvia carbon tax', value: 'Latvia carbon tax'},
              {
                label: 'Liechtenstein carbon tax',
                value: 'Liechtenstein carbon tax'
              },
              {
                label: 'Luxembourg carbon tax',
                value: 'Luxembourg carbon tax'
              },
              {
                label: 'Massachusetts state ETS',
                value: 'Massachusetts state ETS'
              },
              {label: 'Mexico carbon tax', value: 'Mexico carbon tax'},
              {label: 'Mexico pilot ETS', value: 'Mexico pilot ETS'},
              {label: 'Montenegro ETS', value: 'Montenegro ETS'},
              {
                label: 'Netherlands carbon tax',
                value: 'Netherlands carbon tax'
              },
              {
                label: 'New Brunswick carbon tax',
                value: 'New Brunswick carbon tax'
              },
              {label: 'New Brunswick ETS', value: 'New Brunswick ETS'},
              {label: 'New Zealand ETS', value: 'New Zealand ETS'},
              {
                label: 'Newfoundland and Labrador carbon tax',
                value: 'Newfoundland and Labrador carbon tax'
              },
              {
                label: 'Newfoundland and Labrador PSS – ETS',
                value: 'Newfoundland and Labrador PSS – ETS'
              },
              {
                label: 'Northwest Territories carbon tax',
                value: 'Northwest Territories carbon tax'
              },
              {label: 'Norway carbon tax', value: 'Norway carbon tax'},
              {
                label: 'Nova Scotia CaT – ETS',
                value: 'Nova Scotia CaT – ETS'
              },
              {label: 'Ontario EPS – ETS', value: 'Ontario EPS – ETS'},
              {label: 'Oregon ETS', value: 'Oregon ETS'},
              {label: 'Poland carbon tax', value: 'Poland carbon tax'},
              {
                label: 'Portugal carbon tax',
                value: 'Portugal carbon tax'
              },
              {
                label: 'Prince Edward Island carbon tax',
                value: 'Prince Edward Island carbon tax'
              },
              {label: 'Québec CaT – ETS', value: 'Québec CaT – ETS'},
              {
                label: 'Queretaro carbon tax',
                value: 'Queretaro carbon tax'
              },
              {label: 'RGGI – ETS', value: 'RGGI – ETS'},
              {label: 'Saitama ETS', value: 'Saitama ETS'},
              {label: 'Sakhalin ETS', value: 'Sakhalin ETS'},
              {
                label: 'Saskatchewan OBPS – ETS',
                value: 'Saskatchewan OBPS – ETS'
              },
              {label: 'Shanghai pilot ETS', value: 'Shanghai pilot ETS'},
              {label: 'Shenzhen pilot ETS', value: 'Shenzhen pilot ETS'},
              {
                label: 'Singapore carbon tax',
                value: 'Singapore carbon tax'
              },
              {
                label: 'Slovenia carbon tax',
                value: 'Slovenia carbon tax'
              },
              {
                label: 'South Africa carbon tax',
                value: 'South Africa carbon tax'
              },
              {label: 'Spain carbon tax', value: 'Spain carbon tax'},
              {
                label: 'State of Mexico carbon tax',
                value: 'State of Mexico carbon tax'
              },
              {label: 'Sweden carbon tax', value: 'Sweden carbon tax'},
              {
                label: 'Switzerland carbon tax',
                value: 'Switzerland carbon tax'
              },
              {label: 'Switzerland ETS', value: 'Switzerland ETS'},
              {
                label: 'Tamaulipas carbon tax',
                value: 'Tamaulipas carbon tax'
              },
              {label: 'Tianjin pilot ETS', value: 'Tianjin pilot ETS'},
              {label: 'Tokyo CaT – ETS', value: 'Tokyo CaT – ETS'},
              {
                label: 'UK Carbon Price Support',
                value: 'UK Carbon Price Support'
              },
              {label: 'UK ETS', value: 'UK ETS'},
              {label: 'Ukraine carbon tax', value: 'Ukraine carbon tax'},
              {label: 'Uruguay CO₂ tax', value: 'Uruguay CO₂ tax'},
              {label: 'Vietnam ETS', value: 'Vietnam ETS'},
              {
                label: 'Washington CAR – ETS',
                value: 'Washington CAR – ETS'
              },
              {label: 'Yucatan carbon tax', value: 'Yucatan carbon tax'},
              {
                label: 'Zacatecas carbon tax',
                value: 'Zacatecas carbon tax'
              },
              {
                label: 'Other carbon tax, please specify',
                value: 'Other carbon tax, please specify'
              },
              {
                label: 'Other ETS, please specify',
                value: 'Other ETS, please specify'
              }
            ]
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '3.5.2 Provide details of each Emissions Trading Scheme (ETS) your organization is regulated by.',
            tooltip: `As the number of jurisdictions with carbon pricing policies has doubled over the last decade, data users are interested in understanding how organizations are
                      affected by these schemes. This question contextualizes
                      the regulatory landscapes in which organizations operate
                      and the potential for future regulation which may impact
                      an organization’s operations`,
            conditionalVisibility: {
              dependsOn:
                CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q11_VALUE,
              showWhen: ['Alberta TIER - ETS'],
              condition: conditionType.EQUALS,
              fieldName:
                CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q11_VALUE
            }
          },
          {
            fieldType: inputType.ADD_FIXED_ROW_TO_TABLE,
            fieldName:
              CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q12_VALUE,
            dependsOn:
              CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q11_VALUE,
            scopeColumnTitle: 'System name',
            showWhen: ['Alberta TIER - ETS'],
            // relatedTableKey:
            //   CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q11_VALUE,
            // allowMultipleRows: false,
            customFixedRowsBasedOnDependency: {
              dependentFieldName:
                CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q11_VALUE,
              checkDependency: fixRowDependency.Q3_5_2
            },
            fixedRows: [''],
            formList: {
              formFields: [
                // {
                //   type: inputType.SELECT,
                //   fieldName:
                //     CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q12_1_VALUE,
                //   fieldLabel: 'System name',
                //   options: [{value: '', label: ''}],
                //   colSpan: true
                // },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q12_2_VALUE,
                  fieldLabel: '% of Scope 1 emissions covered by the ETS',
                  colSpan: true
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q12_3_VALUE,
                  fieldLabel: '% of Scope 2 emissions covered by the ETS',
                  colSpan: true
                },
                {
                  type: inputType.DATEPICKER,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q12_4_VALUE,
                  fieldLabel: 'Period start date',
                  dateFormat: 'DD-MM-YYYY'
                },
                {
                  type: inputType.DATEPICKER,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q12_5_VALUE,
                  fieldLabel: 'Period end date',
                  dateFormat: 'DD-MM-YYYY'
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q12_6_VALUE,
                  fieldLabel: 'Allowances allocated',
                  colSpan: true
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q12_7_VALUE,
                  fieldLabel: 'Allowances purchased',
                  colSpan: true
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q12_8_VALUE,
                  fieldLabel: 'Verified Scope 1 emissions in metric tons CO2e',
                  colSpan: true
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q12_9_VALUE,
                  fieldLabel: 'Verified Scope 2 emissions in metric tons CO2e',
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q12_10_VALUE,
                  fieldLabel: 'Details of ownership',
                  options: [
                    {
                      label: 'Facilities we own and operate',
                      value: 'Facilities we own and operate'
                    },
                    {
                      label: 'Facilities we own but do not operate',
                      value: 'Facilities we own but do not operate'
                    },
                    {
                      label: 'Facilities we operate but do not own',
                      value: 'Facilities we operate but do not own'
                    },
                    {
                      label: 'Other, please specify',
                      value: 'Other, please specify'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q12_11_VALUE,
                  fieldLabel: 'Comment',
                  colSpan: true,
                  textLimit: 2400
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '3.5.3 Complete the following table for each of the tax systems you are regulated by.',
            tooltip:
              'This question allows data users to track and analyze in a detailed and consistent manner the organizations’ participation in carbon tax systems, as well as what costs they currently bear.',
            conditionalVisibility: {
              dependsOn:
                CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q11_VALUE,
              showWhen: ['Baja California carbon tax'],
              condition: conditionType.EQUALS,
              fieldName:
                CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q11_VALUE
            }
          },
          {
            fieldType: inputType.ADD_FIXED_ROW_TO_TABLE,
            fieldName:
              CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q13_VALUE,
            dependsOn:
              CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q11_VALUE,
            showWhen: ['Baja California carbon tax'],
            // relatedTableKey:
            //   CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q11_VALUE,
            // allowMultipleRows: false,
            scopeColumnTitle: 'Tax system',
            customFixedRowsBasedOnDependency: {
              dependentFieldName:
                CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q11_VALUE,
              checkDependency: fixRowDependency.Q3_5_2
            },
            fixedRows: [''],
            formList: {
              formFields: [
                // {
                //   type: inputType.WYSIWYG,
                //   fieldName:
                //     CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q13_1_VALUE,
                //   fieldLabel: 'Tax system ',
                //   colSpan: true
                // },
                {
                  type: inputType.DATEPICKER,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q13_2_VALUE,
                  fieldLabel: 'Period start date',
                  dateFormat: 'DD-MM-YYYY'
                },
                {
                  type: inputType.DATEPICKER,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q13_3_VALUE,
                  fieldLabel: 'Period end date',
                  dateFormat: 'DD-MM-YYYY'
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q13_4_VALUE,
                  fieldLabel: '% of total Scope 1 emissions covered by tax',
                  colSpan: true
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q13_5_VALUE,
                  fieldLabel: 'Total cost of tax paid',
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q13_6_VALUE,
                  fieldLabel: 'Comment',
                  colSpan: true,
                  textLimit: 2400
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            // dependsOn:
            // CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q10_VALUE,
            // conditionalVisibility: {
            dependsOn:
              CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q10_VALUE,
            showWhen: ['Yes'],
            condition: conditionType.EQUALS,

            //  },
            fieldType: inputType.WYSIWYG,
            fieldName:
              CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q14_VALUE,
            fieldLabel:
              '3.5.4 What is your strategy for complying with the systems you are regulated by or anticipate being',
            tooltip:
              'This question provides data users with insight into an organization’s long-term compliance and regulatory risk management strategy for the carbon pricing systems they are regulated by or anticipate being regulated by.',
            heading: inputType.HEADING,
            colSpan: true,
            textLimit: 5000
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '3.6 Have you identified any environmental opportunities which have had a substantive effect on your organization in the reporting year, or are anticipated to have a substantive effect on your organization in the future?',
            tooltip: `It is crucial for investors and data users to know whether your organization has
                  identified any environmental opportunities within your direct operations or across your value chain with the potential to have substantive effects on your organization. This includes both opportunities which have already had an effect in the reporting year, as well as opportunities which may have an effect in the future. If no environmental
                  opportunities with substantive effects have been identified as part of your assessment, it is equally crucial for data users to understand how and why your organization has
                  concluded that you are not presented with any opportunities.`
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q15_VALUE,
            allowMultipleRows: false,
            formList: {
              formFields: [
                {
                  type: inputType.SELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q15_1_VALUE,
                  fieldLabel: 'Environmental issue',
                  options: [
                    {value: 'Climate change', label: 'Climate change'},
                    {value: 'Forests', label: 'Forests'},
                    {value: 'Water', label: 'Water'},
                    {
                      value: 'Biodiversity [M-B only]',
                      label: 'Biodiversity [M-B only]'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q15_2_VALUE,
                  fieldLabel: 'Environmental opportunities identified',
                  options: [
                    {
                      label:
                        'Yes, we have identified opportunities, and some/all are being realized',
                      value:
                        'Yes, we have identified opportunities, and some/all are being realized'
                    },
                    {
                      label:
                        'Yes, we have identified opportunities but are unable to realize them',
                      value:
                        'Yes, we have identified opportunities but are unable to realize them'
                    },
                    {label: 'No', value: 'No'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q15_3_VALUE,
                  fieldLabel:
                    'Primary reason why your organization does not consider itself to have environmental opportunities',
                  options: [
                    {
                      label:
                        'Opportunities exist, but none anticipated to have a substantive effect on organization',
                      value:
                        'Opportunities exist, but none anticipated to have a substantive effect on organization'
                    },
                    {
                      label: 'Evaluation in progress',
                      value: 'Evaluation in progress'
                    },
                    {
                      label:
                        'Lack of internal resources, capabilities, or expertise (e.g., due to organization size)',
                      value:
                        'Lack of internal resources, capabilities, or expertise (e.g., due to organization size)'
                    },
                    {
                      label: 'No standardized procedure',
                      value: 'No standardized procedure'
                    },
                    {
                      label: 'Not an immediate strategic priority',
                      value: 'Not an immediate strategic priority'
                    },
                    {
                      label: 'Judged to be unimportant or not relevant',
                      value: 'Judged to be unimportant or not relevant'
                    },
                    {
                      label: 'Other, please specify',
                      value: 'Other, please specify'
                    }
                  ],
                  colSpan: true,
                  disabledOn: {
                    fieldName:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q15_2_VALUE,
                    condition: conditionType.EQUALS,
                    values: [
                      'Yes, we have identified opportunities, and some/all are being realized'
                    ]
                  }
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q15_4_VALUE,
                  fieldLabel: 'Please explain',
                  colSpan: true,
                  textLimit: 2500,
                  disabledOn: {
                    fieldName:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q15_2_VALUE,
                    condition: conditionType.EQUALS,
                    values: [
                      'Yes, we have identified opportunities, and some/all are being realized'
                    ]
                  }
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '3.6.1 Provide details of the environmental opportunities identified which have had a substantive effect on your organization in the reporting year, or are anticipated to have a substantive effect on your organization in the future',
            tooltip: `Your response to this question provides data users with an overview of the environmental
                    opportunities which have had a substantive effect on your organization in the reporting year or are estimated to have a substantive effect in the future. Information on the current and estimated potential scale of these opportunities, as well as your response strategy to seize
                    these opportunities, enable data users to see how your organization is implementing corporate actions to improve business 
                    resilience and environmental stewardship.`,
            conditionalVisibility: {
              dependsOn:
                CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q15_VALUE,
              showWhen: [
                'Yes, we have identified opportunities, and some/all are being realized',
                'Yes, we have identified opportunities but are unable to realize them'
              ],
              condition: conditionType.EQUALS,
              fieldName:
                CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q15_2_VALUE
            }
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_VALUE,
            dependsOn:
              CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q15_2_VALUE,
            showWhen: [
              'Yes, we have identified opportunities, and some/all are being realized',
              'Yes, we have identified opportunities but are unable to realize them'
            ],
            relatedTableKey:
              CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q15_VALUE,
            formList: {
              formFields: [
                {
                  type: inputType.SELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_1_VALUE,
                  fieldLabel: 'Environmental issue the opportunity relates to',
                  options: [
                    {value: 'Climate change', label: 'Climate change'},
                    {value: 'Forests', label: 'Forests'},
                    {value: 'Water', label: 'Water'},
                    {
                      value: 'Biodiversity [M-B only]',
                      label: 'Biodiversity [M-B only]'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_2_VALUE,
                  fieldLabel: 'Opportunity identifier',
                  options: OPPORTUNITY_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_3_VALUE,
                  fieldLabel: 'Commodity',
                  options: [
                    {label: 'Timber products', value: 'Timber products'},
                    {label: 'Palm oil', value: 'Palm oil'},
                    {label: 'Cattle products', value: 'Cattle products'},
                    {label: 'Cocoa', value: 'Cocoa'},
                    {label: 'Coffee', value: 'Coffee'},
                    {label: 'Soy', value: 'Soy'},
                    {label: 'Rubber', value: 'Rubber'},
                    {
                      label: 'Other, please specify (FS only)',
                      value: 'Other, please specify (FS only)'
                    },
                    {label: 'Not applicable', value: 'Not applicable'}
                  ],
                  colSpan: true,
                  disabledOn: {
                    fieldName:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_1_VALUE,
                    condition: conditionType.NOT,
                    values: ['Forests']
                  },
                  requiredOn: {
                    fieldName:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_1_VALUE,
                    condition: conditionType.EQUALS,
                    values: ['Forests'],
                    dependentValue:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_1_VALUE
                  }
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_4_VALUE,
                  fieldLabel:
                    'Opportunity type and primary environmental opportunity driver',
                  options:
                    OPPORTUNITY_TYPE_PRIMARY_ENVIRONMENTAL_OPPORTUNITY_DRIVER_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_5_VALUE,
                  fieldLabel: 'Value chain stage where the opportunity occurs',
                  options: [
                    {label: 'Direct operations', value: 'Direct operations'},
                    {
                      label: 'Downstream value chain [not shown to FS]',
                      value: 'Downstream value chain [not shown to FS]'
                    },
                    {
                      label: 'Upstream value chain',
                      value: 'Upstream value chain'
                    },
                    {
                      label: 'Banking portfolio [FS only]',
                      value: 'Banking portfolio [FS only]'
                    },
                    {
                      label: 'Insurance underwriting portfolio [FS only]',
                      value: 'Insurance underwriting portfolio [FS only]'
                    },
                    {
                      label: 'Investing (Asset manager) portfolio [FS only]',
                      value: 'Investing (Asset manager) portfolio [FS only]'
                    },
                    {
                      label: 'Investing (Asset owner) portfolio [FS only]',
                      value: 'Investing (Asset owner) portfolio [FS only]'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_6_VALUE,
                  fieldLabel: 'Country/area where the opportunity occurs',
                  options: COUNTRY_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_7_VALUE,
                  fieldLabel: 'River basin where the opportunity occurs',
                  options: RIVER_BASIN_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_8_VALUE,
                  fieldLabel: 'Mining project ID',
                  options: PROJECT_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_9_VALUE,
                  fieldLabel: 'Organization specific description',
                  colSpan: true,
                  textLimit: 1500
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_10_VALUE,
                  fieldLabel: 'Primary financial effect of the opportunity',
                  options: [
                    {
                      label: 'Reduced direct costs',
                      value: 'Reduced direct costs'
                    },
                    {
                      label: 'Reduced indirect (operating) costs',
                      value: 'Reduced indirect (operating) costs'
                    },
                    {
                      label:
                        'Increased revenues resulting from increased demand for products and services',
                      value:
                        'Increased revenues resulting from increased demand for products and services'
                    },
                    {
                      label:
                        'Increased revenues through access to new and emerging markets',
                      value:
                        'Increased revenues through access to new and emerging markets'
                    },
                    {
                      label:
                        'Increased revenues resulting from increased production capacity',
                      value:
                        'Increased revenues resulting from increased production capacity'
                    },
                    {
                      label: 'Increased revenue resulting from price premiums',
                      value: 'Increased revenue resulting from price premiums'
                    },
                    {
                      label:
                        'Increased revenue resulting from direct payments from downstream companies',
                      value:
                        'Increased revenue resulting from direct payments from downstream companies'
                    },
                    {
                      label: 'Increased access to capital',
                      value: 'Increased access to capital'
                    },
                    {
                      label:
                        'Increased access to capital at lower/more favourable rates',
                      value:
                        'Increased access to capital at lower/more favourable rates'
                    },
                    {
                      label: 'Increased value of fixed assets',
                      value: 'Increased value of fixed assets'
                    },
                    {
                      label: 'Increased diversification of financial assets',
                      value: 'Increased diversification of financial assets'
                    },
                    {
                      label:
                        'Increased portfolio value due to upward revaluation of assets [FS only]',
                      value:
                        'Increased portfolio value due to upward revaluation of assets [FS only]'
                    },
                    {
                      label: 'Returns on investment in low-emission technology',
                      value: 'Returns on investment in low-emission technology'
                    },
                    {
                      label: 'Other, please specify',
                      value: 'Other, please specify'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_11_VALUE,
                  fieldLabel:
                    'Time horizon over which the opportunity is anticipated to have a substantive effect on the organization',
                  options: [
                    {label: 'Short-term', value: 'Short-term'},
                    {label: 'Medium-term', value: 'Medium-term'},
                    {label: 'Long-term', value: 'Long-term'},
                    {
                      label:
                        'The opportunity has already had a substantive effect on our organization in the reporting year',
                      value:
                        'The opportunity has already had a substantive effect on our organization in the reporting year'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_12_VALUE,
                  fieldLabel:
                    'Likelihood of the opportunity having an effect within the anticipated time horizon',
                  options: [
                    {label: 'Virtually certain', value: 'Virtually certain'},
                    {label: 'Very likely', value: 'Very likely'},
                    {label: 'Likely', value: 'Likely'},
                    {
                      label: 'More likely than not',
                      value: 'More likely than not'
                    },
                    {
                      label: 'About as likely as not',
                      value: 'About as likely as not'
                    },
                    {label: 'Unlikely', value: 'Unlikely'},
                    {label: 'Very unlikely', value: 'Very unlikely'},
                    {
                      label: 'Exceptionally unlikely',
                      value: 'Exceptionally unlikely'
                    },
                    {label: 'Unknown', value: 'Unknown'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_13_VALUE,
                  fieldLabel: 'Magnitude',
                  options: [
                    {label: 'High', value: 'High'},
                    {label: 'Medium-high', value: 'Medium-high'},
                    {label: 'Medium', value: 'Medium'},
                    {label: 'Medium-low', value: 'Medium-low'},
                    {label: 'Low', value: 'Low'},
                    {label: 'Unknown', value: 'Unknown'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_14_VALUE,
                  fieldLabel:
                    'Effect of the opportunity on the financial position, financial performance and cash flows of the organization in the reporting period',
                  colSpan: true,
                  textLimit: 2500,
                  disabledOn: {
                    fieldName:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_11_VALUE,
                    condition: conditionType.EMPTY,
                    values: []
                  }
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_15_VALUE,
                  fieldLabel:
                    'Anticipated effect of the opportunity on the financial position, financial performance and cash flows of the organization in the selected future time horizons',
                  colSpan: true,
                  textLimit: 2500,
                  disabledOn: {
                    fieldName:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_11_VALUE,
                    condition: conditionType.EMPTY,
                    values: []
                  }
                },
                {
                  type: inputType.RADIOGROUP,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_16_VALUE,
                  fieldLabel:
                    'Are you able to quantify the financial effects of the opportunity?',
                  options: ['Yes', 'No'],
                  colSpan: true
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_17_VALUE,
                  fieldLabel:
                    'Financial effect figure in the reporting period (currency)',
                  colSpan: true,
                  roundTo: 2,
                  disabledOn: {
                    fieldName:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_16_VALUE,
                    condition: conditionType.NOT,
                    values: ['Yes']
                  },
                  requiredOn: {
                    fieldName:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_16_VALUE,
                    condition: conditionType.EQUALS,
                    values: ['Yes'],
                    dependentValue:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_16_VALUE
                  }
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_18_VALUE,
                  fieldLabel:
                    'Anticipated financial effect figure in the short-term – minimum (currency)',
                  colSpan: true,
                  roundTo: 2,
                  disabledOn: {
                    fieldName:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_16_VALUE,
                    condition: conditionType.NOT,
                    values: ['Yes']
                  },
                  requiredOn: {
                    fieldName:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_16_VALUE,
                    condition: conditionType.EQUALS,
                    values: ['Yes'],
                    dependentValue:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_16_VALUE
                  }
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_19_VALUE,
                  fieldLabel:
                    'Anticipated financial effect figure in the short-term – maximum (currency)',
                  colSpan: true,
                  roundTo: 2,
                  disabledOn: {
                    fieldName:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_16_VALUE,
                    condition: conditionType.NOT,
                    values: ['Yes']
                  },
                  requiredOn: {
                    fieldName:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_16_VALUE,
                    condition: conditionType.EQUALS,
                    values: ['Yes'],
                    dependentValue:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_16_VALUE
                  }
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_20_VALUE,
                  fieldLabel:
                    'Anticipated financial effect figure in the medium-term – minimum (currency)',
                  colSpan: true,
                  roundTo: 2,
                  disabledOn: {
                    fieldName:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_16_VALUE,
                    condition: conditionType.NOT,
                    values: ['Yes']
                  },
                  requiredOn: {
                    fieldName:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_16_VALUE,
                    condition: conditionType.EQUALS,
                    values: ['Yes'],
                    dependentValue:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_16_VALUE
                  }
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_21_VALUE,
                  fieldLabel:
                    'Anticipated financial effect figure in the medium-term – maximum (currency)',
                  colSpan: true,
                  roundTo: 2,
                  disabledOn: {
                    fieldName:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_16_VALUE,
                    condition: conditionType.NOT,
                    values: ['Yes']
                  },
                  requiredOn: {
                    fieldName:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_16_VALUE,
                    condition: conditionType.EQUALS,
                    values: ['Yes'],
                    dependentValue:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_16_VALUE
                  }
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_22_VALUE,
                  fieldLabel:
                    'Anticipated financial effect figure in the long-term – minimum (currency)',
                  colSpan: true,
                  roundTo: 2,
                  disabledOn: {
                    fieldName:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_16_VALUE,
                    condition: conditionType.NOT,
                    values: ['Yes']
                  },
                  requiredOn: {
                    fieldName:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_16_VALUE,
                    condition: conditionType.EQUALS,
                    values: ['Yes'],
                    dependentValue:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_16_VALUE
                  }
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_23_VALUE,
                  fieldLabel:
                    'Anticipated financial effect figure in the long-term – maximum (currency)',
                  colSpan: true,
                  roundTo: 2,
                  disabledOn: {
                    fieldName:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_16_VALUE,
                    condition: conditionType.NOT,
                    values: ['Yes']
                  },
                  requiredOn: {
                    fieldName:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_16_VALUE,
                    condition: conditionType.EQUALS,
                    values: ['Yes'],
                    dependentValue:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_16_VALUE
                  }
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_24_VALUE,
                  fieldLabel: 'Explanation of financial effect figure',
                  colSpan: true,
                  textLimit: 2500,
                  disabledOn: {
                    fieldName:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_17_VALUE,
                    condition: conditionType.EMPTY,
                    values: []
                  },
                  requiredOn: {
                    fieldName:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_17_VALUE,
                    condition: conditionType.EQUALS,
                    values: [],
                    dependentValue:
                      CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_17_VALUE
                  }
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_25_VALUE,
                  fieldLabel: 'Cost to realize opportunity',
                  colSpan: true,
                  roundTo: 2
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_26_VALUE,
                  fieldLabel: 'Explanation of cost calculation',
                  colSpan: true,
                  textLimit: 2500
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q16_27_VALUE,
                  fieldLabel: 'Strategy to realize opportunity',
                  colSpan: true,
                  textLimit: 2500
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '3.6.2 Provide the amount and proportion of your financial metrics in the reporting year that are aligned with the substantive effects of environmental opportunities.',
            tooltip: `This question provides investors and data users with a more complete picture of alignment of
                      finance the substantive effects of environmental opportunities. The proportion of individual
                      financial metrics that are aligned with opportunities can inform strategy to addressing each
                      environmental issue due to each metric’s implications and distinctive role within an
                      organization's financial accounting and strategy. `,
            conditionalVisibility: {
              dependsOn:
                CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q15_VALUE,
              showWhen: [
                'Yes, we have identified opportunities, and some/all are being realized',
                'Yes, we have identified opportunities but are unable to realize them'
              ],
              condition: conditionType.EQUALS,
              fieldName:
                CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q15_2_VALUE
            }
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q17_VALUE,
            dependsOn:
              CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q15_2_VALUE,
            showWhen: [
              'Yes, we have identified opportunities, and some/all are being realized',
              'Yes, we have identified opportunities but are unable to realize them'
            ],
            relatedTableKey:
              CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q15_VALUE,
            formList: {
              formFields: [
                {
                  type: inputType.SELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q17_1_VALUE,
                  fieldLabel: 'Environmental issue',
                  options: [
                    {value: 'Climate change', label: 'Climate change'},
                    {value: 'Water', label: 'Water'},
                    {value: 'Forests', label: 'Forests'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q17_2_VALUE,
                  fieldLabel: 'Financial metric',
                  options: [
                    {label: 'Assets', value: 'Assets'},
                    {label: 'Revenue', value: 'Revenue'},
                    {label: 'CAPEX', value: 'CAPEX'},
                    {label: 'OPEX', value: 'OPEX'},
                    {
                      label: 'Other, please specify',
                      value: 'Other, please specify'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q17_3_VALUE,
                  fieldLabel:
                    'Amount of financial metric aligned with opportunities for this environmental issue (unit currency as selected in 1.2)',
                  colSpan: true,
                  roundTo: 2
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q17_4_VALUE,
                  fieldLabel:
                    '% of total financial metric aligned with opportunities for this environmental issue',
                  options: [
                    {label: 'Less than 1%', value: 'Less than 1%'},
                    {label: '1-10%', value: '1-10%'},
                    {label: '11-20%', value: '11-20%'},
                    {label: '21-30%', value: '21-30%'},
                    {label: '31-40%', value: '31-40%'},
                    {label: '41-50%', value: '41-50%'},
                    {label: '51-60%', value: '51-60%'},
                    {label: '61-70%', value: '61-70%'},
                    {label: '71-80%', value: '71-80%'},
                    {label: '81-90%', value: '81-90%'},
                    {label: '91-99%', value: '91-99%'},
                    {label: '100%', value: '100%'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CORPORATE_DISCLOSURE_FIELD_NAMES.CORPORATE_DISCLOSURE_Q17_5_VALUE,
                  fieldLabel: 'Explanation of financial figures',
                  colSpan: true,
                  textLimit: 2500
                }
              ]
            }
          }
        ]
      }
    ]
  }
];
